<template>
  <main>
    <!-- Micro-optimization: add preconnect headers -->
    <Link
      v-if="useFutureHome"
      rel="preconnect"
      href="https://images.nextory.com"
    />
    <Link
      v-if="useFutureHome"
      rel="preconnect"
      href="https://assets.nextory.com"
    />

    <template v-if="!useFutureHero">
      <LazyHomeHeading />
    </template>
    <template v-else>
      <LazyStaticHeroStandardBlock />
    </template>

    <template v-if="useFutureHome">
      <LazyHomeContentParallax />
      <LazyHomeContentActivitySlider />
    </template>
    <template v-else>
      <div class="bg--sand-200 px-24 py-32 py-md-48 py-lg-64">
        <div class="text-center">
          <p class="subtitle--medium text--sand-800">
            {{ $t('homepage.more_about.title') }}
          </p>
        </div>

        <LazyContentIllustratedDetails
          v-for="(element, i) in illustratedDetailsSection"
          :key="i"
          :title="element.title"
          :details="element.details"
          :src="element.src"
          :alt="element.alt"
          :img-left="element.imgLeft"
          width="448"
          height="358"
        />
      </div>
    </template>

    <template v-if="useFutureHome">
      <LazyHomeContentShowcase />
      <LazyHomeContentCustomerRating />
    </template>

    <LazyContentSubscriptionsBlock />

    <template v-if="useFutureHome">
      <LazyContentFrequentlyAskedQuestionsBlock />
    </template>
    <template v-else>
      <LazyHomeContentCustomerRating />
    </template>

    <LazyContentScrollingTextBlock />

    <StickyButton>
      <NuxtLink
        :to="localePath({ name: 'register' })"
        class="sticky-button button button--primary"
        data-test="signup-sticky-btn"
      >
        {{
          $t(ctaButtonKey, {
            trialDays: $store.state.webConfig.config?.trialDays,
          })
        }}
      </NuxtLink>
    </StickyButton>
  </main>
</template>

<script>
export default defineNuxtComponent({
  setup() {
    definePageMeta({
      layout: 'transparent-header',
    })

    const { $i18n, $store } = useNuxtApp()
    const { query } = useCurrentRoute()

    const title = $i18n.t('meta.home.title', {
      trialDays: $store.state.webConfig.config?.trialDays,
    })
    const description = $i18n.t('meta.home.description', {
      trialDays: $store.state.webConfig.config?.trialDays,
    })

    const elements = metaElements(title, description)

    // Mark page as not indexable if any AB test flag is present in the query
    // This is done to avoid duplicate content issues
    const abTestFlags = ['futureHero']
    if (abTestFlags.some(flag => query[flag] !== undefined)) {
      elements.meta.push({
        name: 'robots',
        content: 'noindex',
      })
    }

    useHead(elements)
  },

  data() {
    return {
      illustratedDetailsSection: {
        firstElement: {
          title: this.$t('homepage.more_about.title.usp_read_or_listen'),
          details: this.$t(
            this.$store.state.webConfig.config?.features.hasMagazines
              ? 'homepage.more_about.body.usp_read_or_listen.magazine_market'
              : 'homepage.more_about.body.usp_read_or_listen'
          ),
          src: this.$hummingbird.marketProperties.img.homepage.more_about_1,
          alt: this.$t('homepage.more_about.img_1.alt'),
        },
        secondElement: {
          title: this.$t('homepage.more_about.title.usp_books_for_family'),
          details: this.$t('homepage.more_about.body.usp_books_for_family'),
          src: this.$hummingbird.marketProperties.img.homepage.more_about_2,
          alt: this.$t('homepage.more_about.img_2.alt'),
          imgLeft: true,
        },
        thirdElement: {
          title: this.$t('homepage.more_about.title.usp_life_better_w_books'),
          details: this.$t('homepage.more_about.body.usp_life_better_w_books'),
          src: this.$hummingbird.marketProperties.img.homepage.more_about_3,
          alt: this.$t('homepage.more_about.img_3.alt'),
        },
      },
    }
  },

  computed: {
    useFutureHome() {
      return (
        this.$route.query.futureHome !== undefined ||
        this.$hummingbird.marketProperties.img.homepage.use_future_home
      )
    },

    ctaButtonKey() {
      return this.$hummingbird.marketProperties.cta
    },

    // The future hero has scrolling books, it's hidden by default
    // ℹ Query parameter: futureHero
    useFutureHero() {
      if (this.$route.query.futureHero === 'false') {
        return false
      }

      if (this.$route.query.futureHero === 'true') {
        return true
      }

      return this.$hummingbird.marketProperties.img.homepage.use_future_hero
    },
  },

  methods: {
    // This method returns content based on the specified content type and the current country.
    getContentByCountry(contentType) {
      const { country } = parseLocaleIso(this.$i18n.localeProperties.language)

      return this[contentType][country] || {}
    },
  },

  jsonld() {
    return getStructuredSearchAction(this.$nuxt)
  },
})
</script>
